import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import DocumentEditor from "./pages/DocumentEditor";
import Inventory from "./pages/Inventory";
import InventoryItemDetail from "./pages/InventoryItemDetail";
import Classifications from "./pages/Classifications";
import Library from "./pages/Library";
import Settings from "./pages/Settings";
import WordToPdf from "./pages/WordToPdf";
import AITools from "./pages/AITools";
import CashFlowDashboard from "./pages/CashFlowDashboard";
import CashFlowStatement from "./pages/CashFlowStatement";
import CashLedger from "./pages/CashLedger";
import BalanceSheet from "./pages/BalanceSheet";
import IncomeStatement from "./pages/IncomeStatement";
import DataAnalytics from "./pages/DataAnalytics";
import AccountsReceivable from "./pages/AccountsReceivable";
import QuickSale from "./pages/QuickSale";
import Clients from "./pages/Clients";
import AuditTrail from "./pages/AuditTrail";
import UnifiedAccounting from "./pages/UnifiedAccounting";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SharedDocument from "./pages/SharedDocument";
import AcceptInvitation from "./pages/AcceptInvitation";
import PaymentSuccess from "./pages/PaymentSuccess";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import CompleteRegistration from "./pages/CompleteRegistration";
// Landing pages
import LandingHome from "./pages/landing/LandingHome";
import Features from "./pages/landing/Features";
import Pricing from "./pages/landing/Pricing";
import About from "./pages/landing/About";
import Contact from "./pages/landing/Contact";
import PrivacyPolicy from "./pages/landing/PrivacyPolicy";
import TermsOfService from "./pages/landing/TermsOfService";
import Admin from "./pages/Admin";
import { Loader2 } from "lucide-react";

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { loading, isAuthenticated, hasActiveSubscription, subscriptionLoading } = useAuth();
  
  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasActiveSubscription) {
    return <Navigate to="/pricing" replace />;
  }
  
  return children;
}

// Public route (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading, isAuthenticated, hasActiveSubscription, subscriptionLoading } = useAuth();
  
  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Check onboarding status
    if (!user?.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }

    if (!hasActiveSubscription) {
      return <Navigate to="/pricing" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Landing page route (show landing for guests, redirect to dashboard for logged in users)
function LandingPageRoute({ children }) {
  const { user, loading, isAuthenticated, hasActiveSubscription, subscriptionLoading } = useAuth();
  
  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    if (!user?.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }

    if (!hasActiveSubscription) {
      return <Navigate to="/pricing" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing pages (public) */}
      <Route path="/" element={<LandingPageRoute><LandingHome /></LandingPageRoute>} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Auth routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<Navigate to="/pricing" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/shared/:shareId" element={<SharedDocument />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/complete-registration" element={<CompleteRegistration />} />
      
      {/* Admin Portal - separate from user authentication */}
      <Route path="/admin" element={<Admin />} />
      
      {/* Onboarding (requires auth) */}
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      
      {/* Protected routes (dashboard) */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Home />} />
        <Route path="tools/:docType" element={<DocumentEditor />} />
        <Route path="tools/word-to-pdf" element={<WordToPdf />} />
        <Route path="ai-tools" element={<AITools />} />
        <Route path="accounts-receivable" element={<AccountsReceivable />} />
        <Route path="quick-sale" element={<QuickSale />} />
        <Route path="cash-flow" element={<CashFlowDashboard />} />
        <Route path="cash-flow-statement" element={<CashFlowStatement />} />
        <Route path="cash-flow/ledger" element={<CashLedger />} />
        <Route path="cash-flow/balance-sheet" element={<BalanceSheet />} />
        <Route path="cash-flow/income-statement" element={<IncomeStatement />} />
        <Route path="reports/analytics" element={<DataAnalytics />} />
        <Route path="reports/unified" element={<UnifiedAccounting />} />
        <Route path="audit-trail" element={<AuditTrail />} />
        <Route path="clients" element={<Clients />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="inventory/items/:itemId" element={<InventoryItemDetail />} />
        <Route path="inventory/classifications" element={<Classifications />} />
        <Route path="library" element={<Library />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <ThemeProvider>
            <Toaster position="top-right" richColors />
            <AppRoutes />
          </ThemeProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
